import { FormEventHandler, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { add, format } from "date-fns";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { Toast } from "@sscs/ui";
import { DATE_FIELD_FORMAT } from "@sscs/utils";
import { useGetWhoamiQuery } from "Services";
import { apiErrorToast } from "Utils";
import { useGeneratePersonalAccessTokenMutation } from "../services/personalAccessTokens.api";
import { AccessTokenFormData, ExpiryRange } from "../types";
import { accessTokenFormSchema } from "../util";

type UseAccessTokenForm = {
  errors: FieldErrors<AccessTokenFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
  formMethods: UseFormReturn<AccessTokenFormData>;
  isDateDisabled: boolean;
};

const getExpiryDate = (expiryRange: ExpiryRange): string => {
  switch (expiryRange) {
    case "day-30":
      return format(add(new Date(), { days: 30 }), DATE_FIELD_FORMAT);

    case "day-90":
      return format(add(new Date(), { days: 90 }), DATE_FIELD_FORMAT);

    case "month-6":
      return format(add(new Date(), { months: 6 }), DATE_FIELD_FORMAT);

    case "year-1":
      return format(add(new Date(), { years: 1 }), DATE_FIELD_FORMAT);

    case "custom":
      return format(new Date(), DATE_FIELD_FORMAT);

    case "none":
    default:
      return "";
  }
};

const useGenerateAccessTokenForm = (
  onGenerated: (token: string) => void,
): UseAccessTokenForm => {
  const { data: whoami } = useGetWhoamiQuery();
  const [generateAccessToken] = useGeneratePersonalAccessTokenMutation();

  const formMethods = useForm<AccessTokenFormData>({
    resolver: yupResolver(accessTokenFormSchema),
    defaultValues: {
      name: "",
      expiry: getExpiryDate("day-90"),
      expiryRange: "day-90", // we are pre-selecting 90 days as the default expiry
    },
  });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    watch,
    setValue,
  } = formMethods;

  // this watch and useEffect will auto update the value in the expiry field as the user changes the drop down value
  const expiryRangeValue = watch("expiryRange");

  useEffect(() => {
    setValue("expiry", getExpiryDate(expiryRangeValue));
  }, [expiryRangeValue, setValue]);

  const onSubmit: SubmitHandler<AccessTokenFormData> = async (data) => {
    try {
      if (whoami && !isSubmitting) {
        const response = await generateAccessToken({
          userId: whoami.user_id,
          body: {
            name: data.name,
            expiry: data.expiry || null,
          },
        }).unwrap();

        onGenerated(response.token);
      }
    } catch (error) {
      apiErrorToast(error, "Could not generate access token");
      return;
    }

    Toast.show({
      title: "Access token generated",
      type: "success",
    });
  };

  return {
    onSubmit: handleSubmit(onSubmit),
    errors,
    formMethods,
    isDateDisabled: expiryRangeValue !== "custom",
  };
};

export default useGenerateAccessTokenForm;
